/**
 * Floating Ani refs — the only addressing scheme the model uses for UI (`fe_show` / `fe_close`).
 *
 *   portfolio:port-123                     open (or focus) the entity's detail window
 *   portfolio:port-123/holdings/top        …then switch tab / flash a registered nav target
 *   app:meetings                           open (or focus) an app
 *   app:case-checking/case_check:abc       highlight a row in that list app
 *   review:<taskId>/review_letter          task review workspace, then a nav target
 *   holdings/top                           nav target path in the focused window
 *   global_search:S&P 500                  open global search with that query (verbatim)
 *
 * This module owns ref → window resolution; the model never sends routes, params or window ids.
 */

import type { OpenArtifactSurface } from "@/components/artifacts/openArtifactDestination";
import type { OsWindow } from "@/components/ui/os-layout/OsContext";
import type { OsAniSnapshotSource } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import { aniNavRegistry, showAniNavPath, type AniNavTargetSummary } from "@/components/ui/ani-nav";
import {
  catalogDetailNavIntent,
  getAgentEntityDescriptor,
  listAgentEntityKinds,
  resolveNavIntent,
  type AgentEntityKind,
} from "@/services/agent/agentNavigation";
import {
  detailExpectationForEntity,
  listExpectationForEntity,
  waitForFeOpenVerified,
  type FeOpenExpectation,
} from "@/services/inAppAgent/feOpenVerification";
import type { FeToolResult } from "@/services/agent/feToolRegistry";

export type AniRefHead =
  | { kind: "entity"; entityKind: AgentEntityKind; id: string }
  | { kind: "app"; appId: string }
  | { kind: "review"; taskId: string }
  | { kind: "global_search"; query: string }
  | { kind: "relative" };

export type ParsedAniRef = { head: AniRefHead; segments: string[] };

export type AniRefHandlers = {
  openWindow: (appId: string, params?: Record<string, string>) => FeToolResult | void;
  openArtifact: (artifactId: string) => Promise<FeToolResult | OpenArtifactSurface | void>;
  openTaskReview: (taskId: string) => FeToolResult | void;
  closeWindow: (windowId: string) => boolean;
  focusWindow: (windowId: string) => void;
  updateWindow: (windowId: string, patch: { isMaximized?: boolean; isMinimized?: boolean }) => void;
  openSearch: (query: string) => void;
  readSnapshot: () => OsAniSnapshotSource;
};

const GLOBAL_SEARCH_PREFIX = "global_search:";
const ENTITY_KINDS = new Set<string>(listAgentEntityKinds());

function parseEntitySegment(segment: string): { entityKind: AgentEntityKind; id: string } | null {
  const colon = segment.indexOf(":");
  if (colon <= 0) {
    return null;
  }
  const kind = segment.slice(0, colon);
  const id = segment.slice(colon + 1).trim();
  return ENTITY_KINDS.has(kind) && id ? { entityKind: kind as AgentEntityKind, id } : null;
}

export function parseAniRef(raw: string): ParsedAniRef | { error: string } {
  const ref = raw.trim();
  if (!ref) {
    return { error: "ref is required" };
  }
  if (ref.startsWith(GLOBAL_SEARCH_PREFIX)) {
    return { head: { kind: "global_search", query: ref.slice(GLOBAL_SEARCH_PREFIX.length).trim() }, segments: [] };
  }
  const [first, ...rest] = ref.split("/").map((segment) => segment.trim()).filter(Boolean);
  const colon = first.indexOf(":");
  if (colon < 0) {
    return { head: { kind: "relative" }, segments: [first, ...rest] };
  }
  const prefix = first.slice(0, colon);
  const value = first.slice(colon + 1).trim();
  if (!value) {
    return { error: `Missing id in "${first}"` };
  }
  if (value.startsWith("fe_")) {
    return { error: `"${value}" is a chat session id, not an entity id — use ids from tool results or window refs` };
  }
  if (prefix === "app") {
    return { head: { kind: "app", appId: value }, segments: rest };
  }
  if (prefix === "review") {
    return { head: { kind: "review", taskId: value }, segments: rest };
  }
  const entity = parseEntitySegment(first);
  if (entity) {
    return { head: { kind: "entity", ...entity }, segments: rest };
  }
  return { error: `Unknown ref "${first}" — use app:<app_id>, review:<task_id>, global_search:<query>, or <kind>:<id> with kind one of ${[...ENTITY_KINDS].join(", ")}` };
}

/** Stable ref for an open window (listed in os_context so the model can copy it). */
export function windowRef(window: Pick<OsWindow, "appId" | "params">): string {
  if (window.appId === "task-review") {
    const taskId = String(window.params.taskId ?? window.params.task_id ?? "").trim();
    if (taskId) {
      return `review:${taskId}`;
    }
  }
  const detail = catalogDetailNavIntent(window.appId, window.params);
  if (detail?.mode === "detail") {
    return `${detail.entityKind}:${detail.id}`;
  }
  return `app:${window.appId}`;
}

function windowsInActiveWorkspace(snapshot: OsAniSnapshotSource): OsWindow[] {
  const live = snapshot.getWindowsSnapshot?.();
  if (live) {
    return live.filter((w) => w.workspaceId === snapshot.activeWorkspaceId);
  }
  return (snapshot.windowIdsByWorkspace.get(snapshot.activeWorkspaceId) ?? [])
    .map((id) => snapshot.getWindow(id))
    .filter((w): w is OsWindow => Boolean(w));
}

function unmaximizeOthers(handlers: AniRefHandlers, windowId: string): void {
  for (const window of windowsInActiveWorkspace(handlers.readSnapshot())) {
    if (window.isMaximized && window.id !== windowId) {
      handlers.updateWindow(window.id, { isMaximized: false, isMinimized: true });
    }
  }
  handlers.focusWindow(windowId);
}

type Opened =
  | { ok: true; expectation: FeOpenExpectation | null; externally?: boolean }
  | { ok: false; message: string };

async function openHead(
  head: Exclude<AniRefHead, { kind: "relative" } | { kind: "global_search" }>,
  segments: string[],
  handlers: AniRefHandlers,
): Promise<Opened> {
  if (head.kind === "review") {
    const result = handlers.openTaskReview(head.taskId);
    if (result && !result.success) {
      return { ok: false, message: result.message ?? "Could not open the review" };
    }
    return { ok: true, expectation: { kind: "task-review", taskId: head.taskId } };
  }

  if (head.kind === "entity" && head.entityKind === "artifact") {
    const opened = await handlers.openArtifact(head.id);
    if (opened && typeof opened === "object" && "success" in opened && !opened.success) {
      return { ok: false, message: opened.message ?? "Could not open the output" };
    }
    if (opened === "external_tab") {
      return { ok: true, expectation: null, externally: true };
    }
    return { ok: true, expectation: detailExpectationForEntity("artifact", head.id) };
  }

  const row = head.kind === "app" && segments[0] ? parseEntitySegment(segments[0]) : null;
  if (head.kind === "app" && row) {
    const descriptor = getAgentEntityDescriptor(row.entityKind);
    if (descriptor?.appId !== head.appId || !descriptor.listHighlight) {
      return { ok: false, message: `${head.appId} has no list of ${row.entityKind} — use "${row.entityKind}:${row.id}" to open it` };
    }
  }

  const resolved = head.kind === "app"
    ? row
      ? resolveNavIntent({ mode: "list_highlight", entityKind: row.entityKind, id: row.id })
      : resolveNavIntent({ mode: "app", appId: head.appId })
    : resolveNavIntent({ mode: "detail", entityKind: head.entityKind, id: head.id });
  if (!resolved || resolved.kind !== "window") {
    return { ok: false, message: "Nothing to open for this ref" };
  }
  const result = handlers.openWindow(resolved.target.appId, resolved.target.params);
  if (result && !result.success) {
    return { ok: false, message: result.message ?? `Could not open ${resolved.target.appId}` };
  }
  const expectation = row
    ? listExpectationForEntity(row.entityKind, row.id)
    : head.kind === "app"
      ? { kind: "app" as const, appId: head.appId }
      : detailExpectationForEntity(head.entityKind, head.id);
  return { ok: true, expectation };
}

async function waitForWindow(expectation: FeOpenExpectation, handlers: AniRefHandlers): Promise<string | { error: string }> {
  await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  let verification = await waitForFeOpenVerified(handlers.readSnapshot, expectation);
  if (!verification.ok && verification.windowId) {
    unmaximizeOthers(handlers, verification.windowId);
    verification = await waitForFeOpenVerified(handlers.readSnapshot, expectation, { attempts: 4, delayMs: 100 });
  }
  if (!verification.ok || !verification.windowId) {
    return { error: verification.reason ?? "The window did not appear" };
  }
  return verification.windowId;
}

function shownResult(windowId: string, snapshot: OsAniSnapshotSource, labels: string[]): FeToolResult {
  const window = snapshot.getWindowsSnapshot?.().find((w) => w.id === windowId) ?? snapshot.getWindow(windowId);
  const shown = [window?.title, ...labels].filter(Boolean).join(" · ");
  return {
    success: true,
    message: `Showing ${shown}`,
    data: {
      shown,
      window_ref: window ? windowRef(window) : undefined,
      nav_targets: aniNavRegistry.list(windowId),
    },
  };
}

function navFailure(windowId: string, missing: string, available: AniNavTargetSummary[]): FeToolResult {
  const refs = available.map((target) => target.ref);
  return {
    success: false,
    message: refs.length
      ? `No "${missing}" in this window. Available: ${refs.join(", ")}`
      : `No "${missing}" in this window — it has no navigable targets`,
    data: { window_id: windowId, nav_targets: available },
  };
}

export async function showAniRef(raw: string, handlers: AniRefHandlers): Promise<FeToolResult> {
  const parsed = parseAniRef(raw);
  if ("error" in parsed) {
    return { success: false, message: parsed.error };
  }
  const { head, segments } = parsed;

  if (head.kind === "global_search") {
    handlers.openSearch(head.query);
    return { success: true, message: head.query ? `Searching ${head.query}` : "Opened search", data: { shown: "Search" } };
  }

  let windowId: string;
  let navSegments = segments;
  if (head.kind === "relative") {
    const focused = handlers.readSnapshot().getFocusedWindow();
    if (!focused) {
      return { success: false, message: "No window is focused — start the ref with the window, e.g. portfolio:<id>/…" };
    }
    windowId = focused.id;
  } else {
    let opened = await openHead(head, segments, handlers);
    if (!opened.ok) {
      return { success: false, message: opened.message };
    }
    if (opened.externally) {
      return { success: true, message: "Opened in a new browser tab", data: { shown: raw, opened_externally: true } };
    }
    if (!opened.expectation) {
      return { success: false, message: "Nothing to open for this ref" };
    }
    let found = await waitForWindow(opened.expectation, handlers);
    if (typeof found !== "string") {
      opened = await openHead(head, segments, handlers);
      found = opened.ok && opened.expectation ? await waitForWindow(opened.expectation, handlers) : found;
    }
    if (typeof found !== "string") {
      return { success: false, message: found.error };
    }
    windowId = found;
    handlers.focusWindow(windowId);
    if (head.kind === "app" && segments[0] && parseEntitySegment(segments[0])) {
      navSegments = segments.slice(1);
    }
  }

  if (navSegments.length === 0) {
    return shownResult(windowId, handlers.readSnapshot(), []);
  }
  const nav = await showAniNavPath(windowId, navSegments.join("/"));
  if (!nav.ok) {
    return navFailure(windowId, nav.missing, nav.available);
  }
  return shownResult(windowId, handlers.readSnapshot(), nav.labels);
}

export function closeAniRef(raw: string | undefined, handlers: AniRefHandlers): FeToolResult {
  const snapshot = handlers.readSnapshot();
  let target: OsWindow | undefined;
  if (!raw?.trim()) {
    target = snapshot.getFocusedWindow();
  } else {
    const parsed = parseAniRef(raw);
    if ("error" in parsed) {
      return { success: false, message: parsed.error };
    }
    if (parsed.head.kind === "relative" || parsed.head.kind === "global_search") {
      return { success: false, message: "Pass a window ref (e.g. portfolio:<id> or app:meetings), or omit ref to close the focused window" };
    }
    const headRef = raw.trim().split("/")[0];
    const appId = parsed.head.kind === "app" ? parsed.head.appId : undefined;
    target = windowsInActiveWorkspace(snapshot)
      .reverse()
      .find((window) => windowRef(window) === headRef || window.appId === appId);
  }
  if (!target) {
    return { success: false, message: raw ? `No open window for "${raw}"` : "No window is focused" };
  }
  if (!handlers.closeWindow(target.id)) {
    return { success: false, message: "Could not close the window — it may have unsaved changes" };
  }
  return { success: true, message: `Closed ${target.title}`, data: { closed: windowRef(target) } };
}
